// route-level code splitting
import { NavigationGuard, RouteRecordRaw } from 'vue-router';

import { SEED_TO_SALE_VENDORS, SPLIT_NAMES } from '@/constants';
import { ROUTE_NAMES } from '@/constants/routeNames';
import { RouteTab } from '@/models/routing';
import i18n, { loadTranslations } from '@/plugins/i18n';

const SeedToSale = () => import('@/pages/SeedToSale/index.vue');
const BuyerPackagesView = () => import('@/pages/SeedToSale/BuyerPackages/BuyerPackages.vue');
const PackagesView = () => import('@/pages/SeedToSale/Packages/Packages.vue');
const ProductsView = () => import('@/pages/SeedToSale/Products/index.vue');
const ProductMapping = () => import('@/pages/SeedToSale/ProductMapping/ProductMapping.vue');
const RefreshRequests = () => import('@/pages/SeedToSale/RefreshRequests/index.vue');
const TemplatesView = () => import('@/pages/SeedToSale/Templates/Templates.vue');
const TransfersView = () => import('@/pages/SeedToSale/Transfers/Transfers.vue');

const beforeEnter: NavigationGuard = async (_to, _from) => {
  await Promise.all([loadTranslations('inventory'), loadTranslations('seedToSale')]);
};

export function tabRoutes(): Readonly<RouteTab[]> {
  return [
    {
      to: { name: ROUTE_NAMES.SEED_TO_SALE_PRODUCTS },
      label: i18n.t('seedToSale.products.title'),
      visible: ({ company, userPermissions }) =>
        company.seller &&
        userPermissions.seedToSale?.canAccessSeedToSalePage &&
        userPermissions.seedToSale?.canAccessSeedToSaleProducts,
    },
    {
      to: { name: ROUTE_NAMES.SEED_TO_SALE_PACKAGES },
      label: i18n.t('seedToSale.packages.title'),
      visible: ({ company, userPermissions }) =>
        company.seller &&
        userPermissions.seedToSale?.canAccessSeedToSalePage &&
        userPermissions.seedToSale?.canAccessSeedToSalePackages,
    },
    {
      to: { name: ROUTE_NAMES.SEED_TO_SALE_BUYER_PACKAGES },
      label: i18n.t('seedToSale.packages.title'),
      visible: ({ company, userPermissions }) =>
        company.buyer && userPermissions.seedToSale?.canAccessSeedToSaleIntegration,
    },
    {
      to: { name: ROUTE_NAMES.SEED_TO_SALE_TRANSFERS },
      label: i18n.t('seedToSale.transfers.title'),
      visible: ({ company, isSplitEnabled, userPermissions }) =>
        company.seller &&
        userPermissions.seedToSale?.canAccessSeedToSalePage &&
        userPermissions.seedToSale?.canAccessSeedToSaleTransfers &&
        (isSplitEnabled(SPLIT_NAMES.S2S_TRANSFERS) || isSplitEnabled(SPLIT_NAMES.S2S_TRANSFERS_BIOTRACK)),
    },
    {
      to: { name: ROUTE_NAMES.SEED_TO_SALE_REFRESH_REQUESTS },
      label: ({ integrationType }) => i18n.t('seedToSale.refreshRequests.title', { vendor: integrationType }),
      visible: ({ company, userPermissions }) => company.seller && userPermissions.seedToSale?.canAccessSeedToSalePage,
    },
  ];
}

const routeConfigs: RouteRecordRaw[] = [
  {
    path: 'seed-to-sale',
    component: SeedToSale,
    beforeEnter,
    meta: {
      tabs: tabRoutes,
    },
    children: [
      {
        path: '',
        component: SeedToSale,
        name: ROUTE_NAMES.SEED_TO_SALE,
        meta: {
          title: () => i18n.t('seedToSale.title'),
          description: () => i18n.t('seedToSale.description'),
          permissions: [
            { rules: ['canAccessSeedToSalePage', ({ userActiveCompany }) => Boolean(userActiveCompany?.staffId)] },
          ],
        },
      },
      // Seller Routes
      {
        path: 'packages',
        name: ROUTE_NAMES.SEED_TO_SALE_PACKAGES,
        component: PackagesView,
        meta: {
          title: ({ seedToSaleVendor }) => {
            return i18n.t('seedToSale.seedToSaleManagement', {
              vendor: seedToSaleVendor || SEED_TO_SALE_VENDORS.METRC,
            });
          },
          description: ({ seedToSaleVendor }) =>
            i18n.t('seedToSale.packages.description', { vendor: seedToSaleVendor || SEED_TO_SALE_VENDORS.METRC }),
          permissions: [
            {
              rules: [
                'canAccessSeedToSalePage',
                'canAccessSeedToSalePackages',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.seller && userActiveCompany.staffId),
              ],
            },
          ],
        },
      },
      {
        path: 'products',
        name: ROUTE_NAMES.SEED_TO_SALE_PRODUCTS,
        component: ProductsView,
        meta: {
          title: ({ seedToSaleVendor }) =>
            i18n.t('seedToSale.seedToSaleManagement', { vendor: seedToSaleVendor || SEED_TO_SALE_VENDORS.METRC }),
          description: ({ seedToSaleVendor }) =>
            i18n.t('seedToSale.packages.description', { vendor: seedToSaleVendor || SEED_TO_SALE_VENDORS.METRC }),
          permissions: [
            {
              rules: [
                'canAccessSeedToSalePage',
                'canAccessSeedToSaleProducts',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.seller && userActiveCompany.staffId),
              ],
            },
          ],
        },
      },
      {
        path: 'products/:productId',
        name: ROUTE_NAMES.SEED_TO_SALE_PRODUCTS_EDIT_MAPPING,
        component: ProductMapping,
        meta: {
          title: () => i18n.t('seedToSale.products.productMapping.title'),
          description: () => i18n.t('seedToSale.products.productMapping.description'),
          permissions: [
            {
              rules: [
                'canAccessSeedToSalePage',
                'canAccessSeedToSaleProducts',
                ({ userActiveCompany, integrationType }) =>
                  Boolean(userActiveCompany?.seller && userActiveCompany.staffId && integrationType === 'METRC'),
              ],
            },
          ],
        },
        props: ({ params }) => ({ productId: parseInt(params.productId as string) || 0 }),
      },
      {
        path: 'refresh-requests',
        name: ROUTE_NAMES.SEED_TO_SALE_REFRESH_REQUESTS,
        component: RefreshRequests,
        meta: {
          title: ({ seedToSaleVendor }) =>
            i18n.t('seedToSale.seedToSaleManagement', { vendor: seedToSaleVendor || SEED_TO_SALE_VENDORS.METRC }),
          description: () => i18n.t('seedToSale.packages.description'),
          permissions: [
            {
              rules: [
                'canAccessSeedToSalePage',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.seller && userActiveCompany.staffId),
              ],
            },
          ],
        },
      },
      {
        path: 'transfers',
        name: ROUTE_NAMES.SEED_TO_SALE_TRANSFERS,
        component: TransfersView,
        meta: {
          title: ({ seedToSaleVendor }) =>
            i18n.t('seedToSale.seedToSaleManagement', { vendor: seedToSaleVendor || SEED_TO_SALE_VENDORS.METRC }),
          description: () => i18n.t('seedToSale.packages.description'),
          permissions: [
            {
              rules: [
                'canAccessSeedToSalePage',
                'canAccessSeedToSaleTransfers',
                ({ userActiveCompany, isSplitEnabled }) =>
                  (Boolean(userActiveCompany?.seller && userActiveCompany.staffId) &&
                    isSplitEnabled('trace_pod__s2s_transfers')) ||
                  isSplitEnabled('traceability__biotrack_seller_transfers_page'),
              ],
            },
          ],
        },
      },
      {
        path: 'templates/new',
        name: ROUTE_NAMES.SEED_TO_SALE_TEMPLATES_NEW,
        component: TemplatesView,
        meta: {
          title: () => i18n.t('seedToSale.templates.header', { action: i18n.t('common.create') }),
          permissions: [
            {
              rules: [
                'trace_pod__s2s_transfers',
                'canAccessSeedToSalePage',
                'canAccessSeedToSaleTransfers',
                ({ userActiveCompany, integrationType }) => {
                  return Boolean(userActiveCompany?.seller && userActiveCompany.staffId && integrationType === 'METRC');
                },
              ],
            },
          ],
        },
      },
      {
        path: 'templates/:templateId',
        name: ROUTE_NAMES.SEED_TO_SALE_TEMPLATES_EDIT,
        component: TemplatesView,
        meta: {
          title: () => i18n.t('seedToSale.templates.header', { action: i18n.t('common.edit') }),
          permissions: [
            {
              rules: [
                'trace_pod__s2s_transfers',
                'canAccessSeedToSalePage',
                'canAccessSeedToSaleTransfers',
                ({ userActiveCompany, integrationType }) => {
                  return Boolean(userActiveCompany?.seller && userActiveCompany.staffId && integrationType === 'METRC');
                },
              ],
            },
          ],
        },
        props: ({ params }) => ({ templateId: parseInt(params.templateId as string) || 0 }),
      },
      // Buyer Routes
      {
        path: 'buyer/packages',
        name: ROUTE_NAMES.SEED_TO_SALE_BUYER_PACKAGES,
        component: BuyerPackagesView,
        meta: {
          title: ({ seedToSaleVendor }) => {
            return i18n.t('seedToSale.seedToSaleManagement', {
              vendor: seedToSaleVendor || SEED_TO_SALE_VENDORS.METRC,
            });
          },
          description: ({ seedToSaleVendor }) =>
            i18n.t('seedToSale.packages.description', { vendor: seedToSaleVendor || SEED_TO_SALE_VENDORS.METRC }),
          permissions: [
            {
              rules: [
                'canAccessSeedToSaleIntegration',
                ({ userActiveCompany }) => Boolean(userActiveCompany?.buyer && userActiveCompany.staffId),
              ],
            },
          ],
        },
      },
      // 404
      {
        path: '*',
        name: ROUTE_NAMES.SEED_TO_SALE_WILDCARD,
        redirect: { name: ROUTE_NAMES.ERROR_404 },
      },
    ],
  },
];

export default routeConfigs;
